import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import TransactionsTable from "@/components/TransactionsTable";
import ReferralsPanel from "@/components/ReferralsPanel";
import ServicesPanel from "@/components/ServicesPanel";
import NewMemberDialog from "@/components/NewMemberDialog";
import { SkeletonRows } from "@/components/Skeleton";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import { Search, Plus, Minus, Coins, CheckCircle2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ProfitField, { profitPayload } from "@/components/ProfitField";
import MemberReferralInfo, { useReferralSummary, ReferralBadges } from "@/components/MemberReferralInfo";
import RedeemConfirmDialog from "@/components/RedeemConfirmDialog";
import { ReferralDaysChip, isPendingReferee } from "@/components/ReferralExpiry";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function StatPill({ label, value, accent }) {
  return (
    <div
      className="rounded-xl px-4 py-3"
      style={{
        background: accent ? "rgba(201,162,75,0.10)" : "var(--surface)",
        border: `1px solid ${accent ? "rgba(201,162,75,0.35)" : "var(--border)"}`,
      }}
    >
      <div className="label-caps">{label}</div>
      <div className="mt-1 num-big">{value}</div>
    </div>
  );
}

function SuccessBurst({ show, children }) {
  if (!show) return null;
  return (
    <div className="pop-in inline-flex items-center gap-2 text-sm font-medium text-success">
      <CheckCircle2 className="h-4 w-4" />
      {children}
    </div>
  );
}

/** Placeholder card shown wherever a staff user lacks the permission to use
 *  a feature. Keeps the layout stable so admins understand the shape of the
 *  page without leaking the feature to unauthorised users. */
function DisabledCard({ title, permKey }) {
  return (
    <div
      className="w-card-flat p-6 flex items-start gap-3"
      style={{ borderStyle: "dashed" }}
      data-testid={`perm-disabled-${permKey}`}
    >
      <Lock className="h-4 w-4 mt-0.5" style={{ color: "var(--text-muted)" }} />
      <div className="text-sm" style={{ color: "var(--text-muted)" }}>
        <div className="font-medium" style={{ color: "var(--text)" }}>
          {title} — access disabled
        </div>
        Your admin has turned this permission off for your account.
      </div>
    </div>
  );
}

export default function StaffDashboard() {
  const { hasPerm, user } = useAuth();
  const canEarn = hasPerm("earn");
  const canRedeem = hasPerm("redeem");
  const canViewCustomers = hasPerm("view_customers");
  const canCreateCustomer = hasPerm("create_customer");
  const canViewLedger = hasPerm("view_ledger");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [txs, setTxs] = useState([]);
  const [config, setConfig] = useState(null);
  const [rates, setRates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [earnAmount, setEarnAmount] = useState("");
  const [earnCurrency, setEarnCurrency] = useState("AED");
  const [earnMode, setEarnMode] = useState("default");
  const [earnService, setEarnService] = useState("Flights");
  const [earnPointsOverride, setEarnPointsOverride] = useState("");
  const [earnReason, setEarnReason] = useState("");
  const [earnNotes, setEarnNotes] = useState("");
  const [earnProfit, setEarnProfit] = useState("");
  // Referral-mode fields: the selected member is the referrer; counterparty is the referee.
  const [earnCounterpartyId, setEarnCounterpartyId] = useState("");
  const [redeemPts, setRedeemPts] = useState("");
  const [redeemNotes, setRedeemNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastAction, setLastAction] = useState(null);
  const refSummary = useReferralSummary(selected?.customer_id,
    `${selected?.reward_balance}-${selected?.lifetime_earned}-${lastAction?.msg || ""}`);
  const [pendingOnly, setPendingOnly] = useState(false);
  const [loadingList, setLoadingList] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const jobs = [
          api.get("/config"),
          api.get("/config/exchange-rates"),
          api.get("/config/countries"),
        ];
        // Only fetch the customer list if the staff user has permission.
        // Otherwise a 403 would fill the toast queue on every visit.
        if (canViewCustomers) jobs.unshift(api.get("/customers"));
        const results = await Promise.all(jobs);
        if (canViewCustomers) {
          const [all, c, r, ct] = results;
          setResults(all.data);
          setConfig(c.data);
          setRates(r.data);
          setCountries(ct.data);
        } else {
          const [c, r, ct] = results;
          setConfig(c.data);
          setRates(r.data);
          setCountries(ct.data);
          setResults([]);
        }
      } catch (e) {
        toast.error(formatError(e));
      } finally {
        setLoadingList(false);
      }
    })();
    // canViewCustomers is derived from user.profile.permissions which is
    // refreshed by AuthContext on focus — re-run the load when it flips.
  }, [canViewCustomers]);

  const doSearch = async (e, pending = pendingOnly) => {
    e?.preventDefault();
    try {
      const { data } = await api.get("/customers", { params: { q, referral: pending ? "pending" : undefined } });
      setResults(data);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const loadCustomer = async (cust) => {
    setSelected(cust);
    // pre-select the customer's preferred currency if it exists in the rate table
    if (cust.preferred_currency && rates.some((r) => r.currency === cust.preferred_currency)) {
      setEarnCurrency(cust.preferred_currency);
    } else {
      setEarnCurrency("AED");
    }
    if (!canViewLedger) {
      setTxs([]);
      return;
    }
    try {
      const { data } = await api.get("/transactions", {
        params: { customer_id: cust.customer_id },
      });
      setTxs(data);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const refreshSelected = async () => {
    if (!selected) return;
    const jobs = [api.get(`/customers/${selected.customer_id}`)];
    if (canViewLedger) jobs.push(api.get("/transactions", { params: { customer_id: selected.customer_id } }));
    const [c, tx] = await Promise.all(jobs);
    setSelected(c.data);
    if (tx) setTxs(tx.data);
    setResults((prev) =>
      prev.map((r) => (r.customer_id === c.data.customer_id ? c.data : r))
    );
  };

  const submitEarn = async (e) => {
    e.preventDefault();
    if (!selected) return;
    const preview = previewPreview();
    const autoCalc = preview ? preview.pts : null;
    let finalPts = earnPointsOverride !== ""
      ? parseInt(earnPointsOverride, 10)
      : (autoCalc || 0);
    if (!finalPts || finalPts <= 0) {
      toast.error("Points must be greater than zero");
      return;
    }
    const reason = earnReason.trim();
    if (earnMode === "manual" && !reason) {
      toast.error("Manual reward requires a Reason");
      return;
    }
    if (earnMode !== "manual" && earnMode !== "referral"
        && autoCalc != null && finalPts !== autoCalc && !reason) {
      toast.error(`Overriding the calculated ${autoCalc} pts requires a Reason`);
      return;
    }
    const basePayload = {
      customer_id: selected.customer_id,
      mode: earnMode,
      final_points: finalPts,
      auto_calculated_points: autoCalc,
      reason: reason || null,
      notes: earnNotes || null,
      force: false,
      ...profitPayload(earnProfit),
    };
    if (earnMode !== "manual" && earnMode !== "referral") {
      basePayload.purchase_amount = parseFloat(earnAmount);
      basePayload.currency = earnCurrency;
    }
    if (earnMode === "service") basePayload.service_type = earnService;

    setBusy(true);
    try {
      let { data } = await api.post("/rewards/earn", basePayload);
      if (data.requires_confirmation) {
        if (!window.confirm(data.message + " Proceed?")) {
          setBusy(false);
          return;
        }
        ({ data } = await api.post("/rewards/earn", { ...basePayload, force: true }));
      }
      // Staff submissions are queued for admin approval — balance has NOT changed yet.
      if (data.pending_approval) {
        const msg = `Submitted for approval · ${data.transaction.final_points} pts (pending)`;
        toast.success(msg);
        setLastAction({ kind: "pending", msg });
      } else {
        const msg = `+${data.transaction.final_points} pts · new balance ${data.new_balance}`;
        toast.success(msg);
        if (data.referral_bonuses?.length) {
          toast.success(`Referral bonus awarded · ${data.referral_bonuses.length} entries`);
        }
        setLastAction({ kind: "earn", msg });
      }
      setEarnAmount("");
      setEarnPointsOverride("");
      setEarnReason("");
      setEarnNotes("");
      setEarnProfit("");
      setEarnCounterpartyId("");
      await refreshSelected();
      setTimeout(() => setLastAction(null), 3500);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const cancelPending = async (tx) => {
    const reason = window.prompt("Reason for cancelling this submission?");
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post(`/transactions/${tx.transaction_id}/cancel`, { reason });
      toast.success("Submission cancelled");
      await refreshSelected();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const [confirmRedeem, setConfirmRedeem] = useState(null);
  const submitRedeem = (e) => {
    e.preventDefault();
    if (!selected) return;
    const pts = parseInt(redeemPts, 10);
    if (!pts || pts <= 0) return toast.error("Points must be > 0");
    setConfirmRedeem({ points: pts, notes: redeemNotes });
  };
  const redeemDone = async (data) => {
    const msg = `−${Math.abs(data.transaction.points)} pts · new balance ${data.new_balance}`;
    setConfirmRedeem(null);
    setLastAction({ kind: "redeem", msg });
    setRedeemPts("");
    setRedeemNotes("");
    await refreshSelected();
    setTimeout(() => setLastAction(null), 3500);
  };

  const currentRate = rates.find((r) => r.currency === earnCurrency);
  const activeRule =
    earnMode === "service" && earnService
      ? config?.service_rules?.[earnService] || config?.default_rule
      : config?.default_rule;
  const previewPreview = () => {
    if (earnMode === "manual") return null;
    if (!config || !earnAmount || !currentRate || !activeRule) return null;
    const amt = parseFloat(earnAmount);
    if (isNaN(amt) || amt <= 0) return null;
    const aed = earnCurrency === "AED" ? amt : Math.round(amt * currentRate.rate_to_aed * 100) / 100;
    const rewardAed =
      activeRule.type === "percentage"
        ? aed * (activeRule.value / 100)
        : Number(activeRule.value);
    const pv = Math.max(0.0001, parseFloat(config.point_value_aed) || 1.0);
    const pts = Math.round(rewardAed / pv);
    return { aed, pts };
  };

  const preview = previewPreview();

  // Live label describing the point ↔ AED rate, e.g. "1 pt = 0.50 AED · 1 AED = 2 pts"
  const rateLabel = (() => {
    const pv = Math.max(0.0001, parseFloat(config?.point_value_aed) || 1.0);
    const aedPerPt = pv.toFixed(2);
    const ptsPerAed = 1 / pv;
    const ptsPerAedStr = Number.isInteger(ptsPerAed) ? ptsPerAed.toString() : ptsPerAed.toFixed(2);
    return `1 pt = ${aedPerPt} AED · 1 AED = ${ptsPerAedStr} pts`;
  })();

  return (
    <AppShell
      title="Front Desk"
      subtitle="Search a member, then earn or redeem in any currency."
      actions={
        canCreateCustomer ? (
          <NewMemberDialog
            countries={countries}
            rates={rates}
            onCreated={(c) => {
              setResults((prev) => [c, ...prev]);
              loadCustomer(c);
            }}
            buttonLabel="Enrol member"
          />
        ) : null
      }
    >
      <div
        className="rounded-xl p-3 mb-5 text-sm flex items-start gap-3"
        style={{ background: "var(--bg-soft)", border: "1px solid var(--border)" }}
        data-testid="staff-info-banner"
      >
        <div className="flex-1">
          <div>
            <span className="font-medium">Rewards you add need admin approval</span>{" "}
            <span style={{ color: "var(--text-muted)" }}>
              — points land in the customer's balance only after approval. You can track status on the member's activity below.
            </span>
          </div>
          <div className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>
            Referral points are credited automatically on the referee's first purchase. Use the Referrals panel below only as a fallback.
          </div>
        </div>
      </div>
      <div className="grid lg:grid-cols-[380px_1fr] gap-6" data-testid="staff-dashboard">
        {/* Left: search + list */}
        {canViewCustomers ? (
        <div className="w-card w-card-bordered overflow-hidden">
          <form onSubmit={doSearch} className="p-4 flex gap-2"
                style={{ borderBottom: "1px solid var(--border)" }}>
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2"
                      style={{ color: "var(--text-muted)" }} />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, phone, email or ID…"
                className="pl-9"
                data-testid="customer-search-input"
              />
            </div>
            <Button
              type="submit"
              className="bg-navy hover:opacity-90 text-white rounded-xl"
              data-testid="customer-search-submit"
            >
              Go
            </Button>
          </form>
          <div className="px-4 py-2 flex items-center gap-2" style={{ borderBottom: "1px solid var(--border)" }}>
            <button type="button" onClick={() => { const v = !pendingOnly; setPendingOnly(v); doSearch(null, v); }}
                    aria-pressed={pendingOnly} data-testid="pending-referrals-filter"
                    className="h-7 px-3 rounded-full text-xs font-medium transition-colors"
                    style={pendingOnly ? { background: "var(--navy)", color: "#fff" } : { border: "1px solid var(--border)", color: "var(--text-muted)" }}>
              Pending referrals
            </button>
            {pendingOnly && <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>Awaiting first purchase · soonest expiry first</span>}
          </div>
          {loadingList ? (
            <div className="p-4"><SkeletonRows rows={4} /></div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto">
              {results.length === 0 && (
                <div className="p-6 text-sm text-center"
                     style={{ color: "var(--text-muted)" }}>
                  No matches.
                </div>
              )}
              {results.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => loadCustomer(c)}
                  data-testid={`customer-row-${c.customer_id}`}
                  className="w-full text-left p-4 transition-colors"
                  style={{
                    background:
                      selected?.customer_id === c.customer_id
                        ? "var(--bg-soft)"
                        : "transparent",
                    borderTop: "1px solid var(--border)",
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{c.name}</div>
                      <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                        {c.phone} · {c.email}
                      </div>
                      <div className="font-mono text-[11px] mt-0.5"
                           style={{ color: "var(--text-muted)" }}>
                        {c.customer_id}
                      </div>
                      {isPendingReferee(c) && (
                        <div className="mt-1"><ReferralDaysChip expiresAt={c.referral_expires_at} testid={`referral-days-${c.customer_id}`} /></div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="num-big">{c.reward_balance}</div>
                      <div className="label-caps mt-0.5">pts</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        ) : (
          <DisabledCard title="Member search & list" permKey="view_customers" />
        )}

        {/* Right: customer detail */}
        <div>
          {!selected ? (
            <div
              className="w-card-flat p-16 text-center text-sm"
              style={{ color: "var(--text-muted)", borderStyle: "dashed" }}
            >
              Select a member from the list to view balance and process rewards.
            </div>
          ) : (
            <>
              <div className="w-card w-card-bordered p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="label-caps">Selected member</div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <div className="text-2xl font-bold" style={{ letterSpacing: "-0.02em" }}
                           data-testid="selected-member-name">
                        {selected.name}
                      </div>
                      <ReferralBadges s={refSummary} />
                    </div>
                    <div className="mt-1 text-sm"
                         style={{ color: "var(--text-muted)" }}>
                      {selected.phone} · {selected.email}
                    </div>
                    <div className="font-mono text-xs"
                         style={{ color: "var(--text-muted)" }}>
                      {selected.customer_id}
                      {selected.country_code ? ` · ${selected.country_code}` : ""}
                      {selected.preferred_currency ? ` · ${selected.preferred_currency}` : ""}
                    </div>
                    <MemberReferralInfo summary={refSummary} hideBadges />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <StatPill label="Balance" value={`${selected.reward_balance} pts`} accent />
                    <StatPill label="Lifetime" value={selected.lifetime_earned} />
                  </div>
                </div>

                {config && (
                  <div className="mt-4 text-xs flex items-center gap-2"
                       style={{ color: "var(--text-muted)" }}>
                    <Coins className="h-3.5 w-3.5 gold" />
                    Current default:{" "}
                    <span className="font-medium" style={{ color: "var(--text)" }}>
                      {config.default_rule?.type === "percentage"
                        ? `${config.default_rule?.value}% of AED · ${rateLabel}`
                        : `${config.default_rule?.value} pts flat · ${rateLabel}`}
                    </span>
                  </div>
                )}

                {lastAction && (
                  <div className="mt-4">
                    <SuccessBurst show>{lastAction.msg}</SuccessBurst>
                  </div>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-6 mt-6">
                {canEarn ? (
                <form onSubmit={submitEarn} className="w-card w-card-bordered p-6 space-y-4"
                      data-testid="earn-form">
                  <div className="flex items-center gap-2 justify-between">
                    <div className="flex items-center gap-2">
                      <Plus className="h-4 w-4 text-success" />
                      <h3 className="text-lg font-semibold">Add Reward</h3>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Mode</Label>
                    <Select value={earnMode} onValueChange={(v) => { setEarnMode(v); setEarnPointsOverride(""); setEarnCounterpartyId(""); }}>
                      <SelectTrigger data-testid="earn-mode-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">Default (total invoice)</SelectItem>
                        <SelectItem value="service">Service-Specific</SelectItem>
                        <SelectItem value="manual">Manual (goodwill / bonus)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {earnMode === "service" && (
                    <div className="space-y-1.5">
                      <Label>Service Type</Label>
                      <Select value={earnService} onValueChange={setEarnService}>
                        <SelectTrigger data-testid="earn-service-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["Flights","Hotels","Packages","Visa","Other"].map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {earnMode !== "manual" && earnMode !== "referral" && (
                    <div className="grid grid-cols-[1fr_120px] gap-2">
                      <div className="space-y-1.5">
                        <Label htmlFor="amt">Invoice amount</Label>
                        <Input
                          id="amt"
                          type="number"
                          min="0"
                          step="0.01"
                          value={earnAmount}
                          onChange={(e) => { setEarnAmount(e.target.value); setEarnPointsOverride(""); }}
                          required
                          data-testid="earn-amount-input"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Currency</Label>
                        <Select value={earnCurrency} onValueChange={(v) => { setEarnCurrency(v); setEarnPointsOverride(""); }}>
                          <SelectTrigger data-testid="earn-currency-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {rates.map((r) => (<SelectItem key={r.currency} value={r.currency}>{r.currency}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                  {earnMode !== "referral" && (
                    <ProfitField value={earnProfit} onChange={setEarnProfit} currency={earnMode === "manual" ? "AED" : earnCurrency}
                                 testid="earn-profit-input" />
                  )}
                  {preview && (
                    <div
                      className="rounded-xl p-3 text-xs flex items-center justify-between"
                      style={{ background: "var(--bg-soft)" }}
                      data-testid="earn-preview"
                    >
                      <span style={{ color: "var(--text-muted)" }}>
                        {earnCurrency !== "AED" && <>{parseFloat(earnAmount || 0).toLocaleString()} {earnCurrency} → </>}
                        <span className="font-semibold" style={{ color: "var(--text)" }}>AED {preview.aed.toLocaleString()}</span>
                        {" → "}
                        <span className="font-semibold" style={{ color: "var(--text)" }}>{preview.pts} pts</span>
                      </span>
                      <span className="text-success font-semibold" data-testid="earn-preview-rate">{rateLabel}</span>
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <Label>{earnMode === "manual" ? "Points to award" : "Final points (editable)"}</Label>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={earnPointsOverride !== "" ? earnPointsOverride : (preview?.pts ?? "")}
                      onChange={(e) => setEarnPointsOverride(e.target.value)}
                      placeholder={earnMode === "manual" ? "e.g. 200" : "auto-calculated"}
                      required={earnMode === "manual"}
                      data-testid="earn-final-points-input"
                    />
                  </div>
                  {(earnMode === "manual" ||
                    (preview && earnPointsOverride !== "" && parseInt(earnPointsOverride,10) !== preview.pts)) && (
                    <div className="space-y-1.5">
                      <Label>Reason <span className="text-red-500">*</span></Label>
                      <Input
                        value={earnReason}
                        onChange={(e) => setEarnReason(e.target.value)}
                        placeholder={earnMode === "manual" ? "e.g. Birthday goodwill bonus" : "Why override the auto-calc"}
                        required
                        data-testid="earn-reason-input"
                      />
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <Label htmlFor="notes-e">Notes (optional)</Label>
                    <Textarea
                      id="notes-e"
                      rows={2}
                      value={earnNotes}
                      onChange={(e) => setEarnNotes(e.target.value)}
                      placeholder="e.g. Dubai–Istanbul flight booking"
                      data-testid="earn-notes-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={busy}
                    className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11"
                    data-testid="earn-submit"
                  >
                    {busy ? "Processing…" : "Add points"}
                  </Button>
                </form>
                ) : (
                  <DisabledCard title="Add Reward" permKey="earn" />
                )}

                {canRedeem ? (
                <form onSubmit={submitRedeem} className="w-card w-card-bordered p-6 space-y-4"
                      data-testid="redeem-form">
                  <RedeemConfirmDialog open={!!confirmRedeem} member={selected} points={confirmRedeem?.points || 0} notes={confirmRedeem?.notes}
                                       onCancel={() => setConfirmRedeem(null)} onDone={redeemDone} />
                  <div className="flex items-center gap-2">
                    <Minus className="h-4 w-4 text-amber-700" />
                    <h3 className="text-lg font-semibold">Redeem Points</h3>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="pts">Points to redeem</Label>
                    <Input
                      id="pts"
                      type="number"
                      min="1"
                      step="1"
                      value={redeemPts}
                      onChange={(e) => setRedeemPts(e.target.value)}
                      required
                      data-testid="redeem-points-input"
                    />
                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                      Available:{" "}
                      <span className="font-semibold" style={{ color: "var(--text)" }}>
                        {selected.reward_balance} pts
                      </span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="notes-r">Notes (optional)</Label>
                    <Textarea
                      id="notes-r"
                      rows={2}
                      value={redeemNotes}
                      onChange={(e) => setRedeemNotes(e.target.value)}
                      placeholder="e.g. Visa fee discount"
                      data-testid="redeem-notes-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={busy}
                    className="w-full text-navy rounded-xl h-11 hover:opacity-90"
                    style={{ background: "var(--gold)" }}
                    data-testid="redeem-submit"
                  >
                    {busy ? "Processing…" : "Redeem points"}
                  </Button>
                </form>
                ) : (
                  <DisabledCard title="Redeem Points" permKey="redeem" />
                )}
              </div>

              {canViewLedger ? (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold mb-3">Recent Activity</h3>
                  <TransactionsTable transactions={txs} onCancelPending={cancelPending} />
                </div>
              ) : (
                <div className="mt-8">
                  <DisabledCard title="Recent Activity" permKey="view_ledger" />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {canViewLedger && <div className="mt-10"><ReferralsPanel onChanged={refreshSelected} /></div>}
      <div className="mt-10"><ServicesPanel readOnly /></div>
    </AppShell>
  );
}
